import React from 'react';

import Badge from './Badge';

/**
 * DocumentCard Component
 * 
 * Reusable document card component.
 * Used in RightSidebar and ContlyRightSidebar for displaying documents.
 * 
 * @param {Object} document - Document object with title, filename, badge, etc.
 * @param {function} onClick - Click handler
 * @param {string} theme - Theme ('dark' or 'light')
 * @param {boolean} showSkeleton - Whether to show loading skeleton
 * @param {Object} badgeStyleMap - Optional map of badge types to badge styles
 * @param {function} badgeLabelMap - Optional function to map badge value to label
 */
const DocumentCard = ({
  document,
  onClick,
  theme = 'dark',
  showSkeleton = false,
  badgeStyleMap = null,
  badgeLabelMap = null,
  onDelete
}) => {
  const handleMouseEnter = (e) => {
    if (theme === 'dark') {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 10px 30px -10px rgba(0, 0, 0, 0.5)';
    } else {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.98)';
      e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.6)';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.15), 0 2px 6px rgba(0, 0, 0, 0.12)';
    }
  };

  const handleMouseLeave = (e) => {
    if (theme === 'dark') {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    } else {
      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
      e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.5)';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 23, 42, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)';
    }
  };

  return (
    <div
      className="doc-card group flex flex-col cursor-pointer transition-all duration-300 rounded-2xl p-3 relative"
      draggable="true"
      onDragStart={(e) => {
        const dragData = JSON.stringify({
          id: document.id,
          name: document.title || document.name,
          url: document.url,
          mimeType: document.mimeType
        });
        e.dataTransfer.setData('application/json', dragData);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      onClick={() => onClick && onClick(document)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        background: theme === 'dark'
          ? 'rgba(255, 255, 255, 0.03)'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        WebkitBackdropFilter: theme === 'dark' ? 'blur(12px)' : 'blur(20px)',
        border: theme === 'dark'
          ? '0.5px solid rgba(255, 255, 255, 0.08)'
          : '0.5px solid rgba(71, 85, 105, 0.5)',
        boxShadow: theme === 'light'
          ? '0 2px 8px rgba(15, 23, 42, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)'
          : 'none',
      }}
    >
      {/* Delete Button - Show on hover */}
      {onDelete && (
        <button
          className="doc-delete-btn absolute top-2 right-2 p-1.5 rounded-full z-10 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(document);
          }}
          style={{
            background: theme === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.8)',
            border: 'none',
            color: theme === 'dark' ? '#ff6b6b' : '#ef4444',
            cursor: 'pointer',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
      {/* Badge */}
      {document.badge && (() => {
        const badgeStyle = badgeStyleMap?.[document.badge];
        const badgeLabel = badgeLabelMap
          ? badgeLabelMap(document.badge)
          : document.badge;

        return (
          <Badge
            label={badgeLabel}
            color={document.badgeColor || '#3b82f6'}
            badgeStyle={badgeStyle}
            theme={theme}
          />
        );
      })()}

      {/* Title */}
      <div
        className="doc-card-title text-sm font-semibold mb-1.5"
        style={{
          color: theme === 'dark' ? '#ffffff' : '#0f172a',
        }}
      >
        {document.title}
      </div>

      {/* Skeleton */}
      {showSkeleton ? (
        <div className="doc-skeleton flex flex-col gap-1.5 mb-1.5">
          <div
            className="skeleton-line h-1 rounded-sm animate-pulse"
            style={{
              width: '100%',
              background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
            }}
          />
          <div
            className="skeleton-line h-1 rounded-sm animate-pulse"
            style={{
              width: '80%',
              background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
            }}
          />
          <div
            className="skeleton-line h-1 rounded-sm animate-pulse"
            style={{
              width: '90%',
              background: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)',
            }}
          />
        </div>
      ) : (
        <div className="h-8" />
      )}

      {/* Filename */}
      <div
        className="doc-filename inline-block px-3 py-1 rounded-full text-[11px] mt-1.5 self-start"
        style={{
          background: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.5)',
          color: theme === 'dark' ? '#888888' : '#1e293b',
        }}
      >
        {document.filename}
      </div>
    </div>
  );
};

export default DocumentCard;

